import { AuthStep } from 'constants/authStep';

import { useContext, useState } from 'react';
import { SyntheticEvent } from 'react';

import { useIntrospection } from '@api-platform/admin';
import { Button } from '@mui/material';
import camelcaseKeys from 'camelcase-keys';
import { resendCode } from 'config/requests/auth';
import { APIContext } from 'context/apiContext';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useAuthProvider, useLogin } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { TextField } from 'shared/mui/TextField/TextField';
import { AuthError, AuthErrorCodes } from 'types/embedded/auth';

type Props = {
  callback: (submit: (captchaToken: string) => void) => void;
  updateStep: (step: AuthStep) => void;
  error: string | null;
  setError: (error: string | null) => void;
};

export const LoginSecondFactorForm: React.FC<Props> = ({
  callback,
  updateStep,
  error,
  setError,
}) => {
  const translate = useTypedTranslate();
  const [code, setCode] = useState('');
  const [isResetPasswordEnabled, setIsResetPasswordEnabled] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);

  const hasError = Boolean(error);
  const hasNotification = Boolean(notification);

  const introspect = useIntrospection();
  const login = useLogin();
  const { setUser } = useContext(APIContext);
  const authProvider = useAuthProvider();
  const navigate = useNavigate();

  const onResendCode = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const token = authProvider?.currentStepResponseContent.token;
    if (!token) {
      navigate('login');
    }

    resendCode(token || '')
      .then((data) => {
        setError(null);
        setIsResetPasswordEnabled(false);
        setNotification(data?.message);
      })
      .catch(() => {
        setError(translate('app.login.codeResendUnavailable'));
      });
  };

  const submit = (captchaToken: string) => {
    login({ captchaToken, code })
      .then((user) => {
        setUser({ ...camelcaseKeys(user), isLoaded: true });
      })
      .then(() => {
        introspect();
      })
      .catch((e: AuthError) => {
        if (e.code === AuthErrorCodes.Expired) {
          setIsResetPasswordEnabled(true);
        }
        if (e.code === AuthErrorCodes.Blocked) {
          setError(translate('app.login.codeAttemptsExceeded'));
          updateStep(AuthStep.Credentials);
          return;
        }
        setError(e.title);
      });
  };

  const proxySubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    setError(null);
    callback(submit);
  };

  return (
    <Box>
      {hasError && (
        <Alert severity="error" sx={{ marginBottom: '24px' }}>
          {error}
        </Alert>
      )}
      {hasNotification && (
        <Alert severity="info" sx={{ marginBottom: '24px' }}>
          {notification}
        </Alert>
      )}
      <form noValidate onSubmit={proxySubmit}>
        <TextField
          autoComplete="code"
          autoFocus
          error={hasError}
          fullWidth
          id="code"
          label={translate('app.login.codeFrom')}
          margin="normal"
          name="code"
          onChange={(e) => setCode(e.target.value)}
          size="medium"
          sx={{ marginBottom: '24px !important' }}
          value={code}
          variant="outlined"
        />
        <Button
          color="primary"
          disabled={!isResetPasswordEnabled}
          fullWidth
          onClick={onResendCode}
          sx={{ marginBottom: '16px' }}
          type="button"
          variant="outlined"
        >
          {translate('app.login.requestNewCode')}
        </Button>
        <Button color="primary" fullWidth type="submit" variant="contained">
          {translate('app.login.send')}
        </Button>
      </form>
    </Box>
  );
};
